import {useQuery} from '@apollo/client';
import {useCallback} from 'react';
import {
    GET_REVIEW_TASK_COUNT,
    GetReviewTaskCountResponse,
    GetReviewTaskCountVariables,
} from '../graphql/queries/getReviewTaskCount';
import {
    ReviewTaskTab,
    ReviewBizType,
    ReviewStatus,
    ReviewTaskFiltersTypeInput,
} from '../graphql/types/ReviewTaskType';

/**
 * Tab 计数 hook（PodReview 范式 + 智能跳过）。
 *
 * 行为规则：
 * - 未选 status（statuses 为空/未传）：5 个 tab 各自查询计数（兼容旧行为）。
 * - 选中 status（statuses 非空）：
 *   - 总 tab 直接复用列表 totalElements，不发 RPC。
 *   - 命中 statuses 的 tab：
 *     * 仅 1 个命中 → 复用 listTotalElements（不发 RPC）。
 *     * ≥2 个命中 → 各自独立查询（无法从一个总数拆分）。
 *   - 未命中 statuses 的 tab：必然为 0，skip 不发 RPC。
 *
 * 共享筛选条件（运单号/DSP/时间区间等）始终透传，与列表查询保持一致。
 */
export const useReviewTaskTabCounts = (
    bizType: string = ReviewBizType.CUSTOMER_COMPLAINT,
    sharedFilters?: Omit<ReviewTaskFiltersTypeInput, 'bizType' | 'statuses'>,
    selectedStatuses?: string[],
    listTotalElements?: number,
) => {
    const opts = {
        fetchPolicy: 'no-cache' as const,
        errorPolicy: 'all' as const,
        notifyOnNetworkStatusChange: true,
    };

    const hasStatusFilter = (selectedStatuses?.length ?? 0) > 0;
    const isHit = (status: string): boolean =>
        !hasStatusFilter || (selectedStatuses?.includes(status) ?? false);
    /** 命中 status 是否可以从 listTotalElements 直接复用（命中只有 1 个时） */
    const canReuseListTotal = hasStatusFilter && (selectedStatuses?.length ?? 0) === 1;

    const buildVars = (statuses: string[] | null): GetReviewTaskCountVariables => ({
        filters: {bizType, statuses, ...(sharedFilters ?? {})},
    });

    // 各 tab query 的 skip 规则：
    // - 总 tab：选了 status 时直接复用 listTotalElements，永远 skip。
    // - 其它 tab：未命中 status 时 skip（必然 0）；命中且 canReuseListTotal 时 skip（复用 listTotalElements）。
    const skipForTab = (status: string): boolean => {
        if (!isHit(status)) return true;
        if (canReuseListTotal) return true;
        return false;
    };

    const allQ = useQuery<GetReviewTaskCountResponse, GetReviewTaskCountVariables>(
        GET_REVIEW_TASK_COUNT,
        {variables: buildVars(null), skip: hasStatusFilter, ...opts}
    );
    const pendingQ = useQuery<GetReviewTaskCountResponse, GetReviewTaskCountVariables>(
        GET_REVIEW_TASK_COUNT,
        {variables: buildVars([ReviewStatus.PENDING_REVIEW]), skip: skipForTab(ReviewStatus.PENDING_REVIEW), ...opts}
    );
    const approvedQ = useQuery<GetReviewTaskCountResponse, GetReviewTaskCountVariables>(
        GET_REVIEW_TASK_COUNT,
        {variables: buildVars([ReviewStatus.APPROVED]), skip: skipForTab(ReviewStatus.APPROVED), ...opts}
    );
    const rejectedQ = useQuery<GetReviewTaskCountResponse, GetReviewTaskCountVariables>(
        GET_REVIEW_TASK_COUNT,
        {variables: buildVars([ReviewStatus.REJECTED]), skip: skipForTab(ReviewStatus.REJECTED), ...opts}
    );
    const cancelledQ = useQuery<GetReviewTaskCountResponse, GetReviewTaskCountVariables>(
        GET_REVIEW_TASK_COUNT,
        {variables: buildVars([ReviewStatus.CANCELLED]), skip: skipForTab(ReviewStatus.CANCELLED), ...opts}
    );

    const refetchAll = useCallback(() => {
        if (!hasStatusFilter) allQ.refetch();
        if (!skipForTab(ReviewStatus.PENDING_REVIEW)) pendingQ.refetch();
        if (!skipForTab(ReviewStatus.APPROVED)) approvedQ.refetch();
        if (!skipForTab(ReviewStatus.REJECTED)) rejectedQ.refetch();
        if (!skipForTab(ReviewStatus.CANCELLED)) cancelledQ.refetch();
        // skipForTab 依赖 selectedStatuses（isHit/canReuseListTotal 均由它派生），无需单独列出
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allQ, pendingQ, approvedQ, rejectedQ, cancelledQ, selectedStatuses]);

    /** 单个 tab 的计数解析：先看是否复用 listTotalElements，再看 query 结果 */
    const resolveCount = (
        status: string,
        q: {loading: boolean; data?: GetReviewTaskCountResponse}
    ): number | undefined => {
        if (!isHit(status)) return 0;
        if (canReuseListTotal) return listTotalElements ?? 0;
        return q.loading ? undefined : q.data?.getReviewTaskCount ?? 0;
    };

    const getCount = (tab: ReviewTaskTab): number | undefined => {
        switch (tab) {
            case ReviewTaskTab.ALL:
                if (hasStatusFilter) return listTotalElements ?? 0;
                return allQ.loading ? undefined : allQ.data?.getReviewTaskCount ?? 0;
            case ReviewTaskTab.PENDING:
                return resolveCount(ReviewStatus.PENDING_REVIEW, pendingQ);
            case ReviewTaskTab.APPROVED:
                return resolveCount(ReviewStatus.APPROVED, approvedQ);
            case ReviewTaskTab.REJECTED:
                return resolveCount(ReviewStatus.REJECTED, rejectedQ);
            case ReviewTaskTab.CANCELLED:
                return resolveCount(ReviewStatus.CANCELLED, cancelledQ);
            default:
                return undefined;
        }
    };

    return {
        getCount,
        refetchAll,
        loadingAll:
            allQ.loading ||
            pendingQ.loading ||
            approvedQ.loading ||
            rejectedQ.loading ||
            cancelledQ.loading,
    };
};
